(() => {
  if (customElements.get('mediatech-payment24')) return;
  class MediatechPayment24 extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      try {
        this.config = JSON.parse(this.querySelector('[data-payment24-config]').textContent);
      } catch {
        this.hidden = true;
        return;
      }
      this.button = this.querySelector('[data-payment24-button]');
      this.consent = this.querySelector('[data-payment24-consent]');
      this.error = this.querySelector('[data-payment24-error]');
      this.dialog = this.querySelector('[data-payment24-dialog]');
      this.opener = this.querySelector('[data-payment24-open]');
      this.minimum = this.querySelector('[data-payment24-minimum]');
      this.termInputs = Array.from(this.querySelectorAll?.('[data-payment24-term]') || []);
      this.term = Number(this.termInputs.find(input => input.checked)?.value) || 24;
      this.busy = false;
      this.pendingSelection = false;
      this.selectionFailed = false;
      this.consent.checked = false;
      this.selectionGeneration = (this.selectionGeneration || 0) + 1;
      this.checkoutGeneration = (this.checkoutGeneration || 0) + 1;
      this.scope = this.closest('[data-mt-product-root]') || this.closest('.buy-buttons-block') || this.parentElement;
      this.form = this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
      this.abortController = new AbortController();
      const eventOptions = { signal: this.abortController.signal };
      this.scope.addEventListener('input', event => {
        if (event.target.matches('[data-payment24-term]')) return;
        this.refresh();
      }, eventOptions);
      this.scope.addEventListener('change', () => this.refresh(), eventOptions);
      this.scope.addEventListener('click', () => requestAnimationFrame(() => this.refresh()), eventOptions);
      this.scope.addEventListener('mediatech:variant-change', (event) => {
        const detail = event.detail || {};
        if (String(detail.productId) !== String(this.config.productId)) return;
        const configured = this.config.variants[String(detail.variantId)];
        this.pendingSelection = false;
        this.selectionFailed = !configured || Number(detail.priceCents) !== configured.totalCents;
        if (configured) configured.sourceAvailable = detail.available !== false;
        this.refresh();
      }, eventOptions);
      this.consent.addEventListener('change', () => this.refresh(false), eventOptions);
      this.button.addEventListener('click', () => this.checkout(), eventOptions);
      this.opener?.addEventListener('click', () => this.openDialog(), eventOptions);
      this.querySelector('[data-payment24-close]')?.addEventListener('click', () => this.closeDialog(), eventOptions);
      for (const input of this.termInputs) input.addEventListener('change', () => {
        if (!input.checked) return;
        this.term = Number(input.value);
        this.checkoutGeneration = (this.checkoutGeneration || 0) + 1;
        this.busy = false;
        this.refresh();
      }, eventOptions);
      this.dialog?.addEventListener('close', () => {
        if (!this.dialog.open) this.finishDialog();
      }, eventOptions);
      this.dialog?.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.closeDialog();
      }, eventOptions);
      this.productScope = this.closest('.shopify-section, dialog, product-card') || this.scope;
      this.productScope.addEventListener('shopify:product:select', async (event) => {
        const sourceCard = event.target?.closest('product-card');
        if (sourceCard && !sourceCard.contains(this)) return;
        const productId = String(event.product?.id || '').split('/').pop();
        if (productId && productId !== String(this.config.productId)) return;
        const intendedId = String(event.detail?.variantId || '').split('/').pop();
        const current = this.selected();
        const currentKey = this.key(current.variant, current.quantity);
        if (intendedId && intendedId === String(current.variant?.id) && currentKey === this.selectionKey) return;
        const generation = this.selectionGeneration = (this.selectionGeneration || 0) + 1;
        this.checkoutGeneration = (this.checkoutGeneration || 0) + 1;
        this.busy = false;
        this.pendingSelection = true;
        this.selectionFailed = false;
        if (intendedId && intendedId !== String(current.variant?.id)) this.consent.checked = false;
        this.button.disabled = true;
        this.error.textContent = '';
        try {
          const result = await event.promise;
          if (!this.isConnected || generation !== this.selectionGeneration) return;
          this.pendingSelection = false;
          const id = this.form?.querySelector('[name="id"]')?.value;
          const configured = this.config.variants[String(id)];
          const resource = result?.detail?.resource;
          if (configured && resource) {
            configured.sourceAvailable = resource.available !== false;
            if (Number(resource.price) !== configured.totalCents) this.selectionFailed = true;
          }
          this.refresh();
        } catch {
          if (!this.isConnected || generation !== this.selectionGeneration) return;
          this.pendingSelection = false;
          this.selectionFailed = true;
          this.refresh();
        }
      }, eventOptions);
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.resetNavigationState();
      }, eventOptions);
      window.addEventListener('pagehide', () => this.resetNavigationState(), eventOptions);
      this.observer = new MutationObserver(() => this.refresh());
      this.observer.observe(this.scope, { attributes: true, subtree: true, attributeFilter: ['value', 'data-current-variant'] });
      this.refresh();
    }
    disconnectedCallback() {
      this.closeDialog(false);
      this.abortController?.abort();
      this.observer?.disconnect();
      this.initialized = false;
    }
    resetNavigationState() {
      this.closeDialog(false);
      this.busy = false;
      this.pendingSelection = false;
      this.selectionGeneration = (this.selectionGeneration || 0) + 1;
      this.checkoutGeneration = (this.checkoutGeneration || 0) + 1;
      this.consent.checked = false;
      this.error.textContent = '';
      this.refresh(false);
    }
    selected() {
      const variantInput = this.form?.querySelector('[name="id"]');
      const id = variantInput ? variantInput.value : this.scope.dataset?.currentVariant ?? this.dataset.defaultVariant;
      const quantityValue = this.form?.querySelector('[name="quantity"]')?.value || this.scope.querySelector('[data-mt-qty]')?.value || '1';
      const base = this.config.variants[String(id)];
      const variant = this.planVariant(base, this.term);
      return { variant, quantity: Number(quantityValue) };
    }
    planVariant(base, term) {
      const plan = base?.plans?.[term];
      return base ? plan ? { ...base, ...plan, term } : term === 24 ? { ...base, term } : { ...base, ready: false, term } : undefined;
    }
    eligibleTerms(priceCents) {
      if (!Number.isSafeInteger(priceCents) || priceCents < 24000) return [];
      if (priceCents < 50000) return [12];
      return priceCents < 100000 ? [12, 24] : [12, 24, 36];
    }
    label(name, values = {}) {
      let result = String(this.config.labels[name] || '');
      for (const [key, value] of Object.entries(values)) result = result.replaceAll(`[${key}]`, String(value));
      return result;
    }
    key(variant, quantity) {
      return variant ? `${variant.id}:${quantity}:${variant.term || this.term || 24}` : '';
    }
    textForTerm(value, term = this.term) {
      return String(value || '').replace(/\b24\b/g, String(term)).replace(/\b23\b/g, String(term - 1)).replace(/\b22\b/g, String(term - 2));
    }
    openDialog() {
      if (!this.dialog || this.dialog.open || this.opener.disabled) return;
      const current = this.selected();
      const base = current.variant && this.config.variants[String(current.variant.id)];
      const available = this.eligibleTerms(base?.totalCents).filter(term => this.valid(this.planVariant(base, term), current.quantity));
      this.term = available.includes(24) ? 24 : available[0];
      if (!this.term) return;
      this.consent.checked = false;
      this.error.textContent = '';
      this.refresh();
      this.previousFocus = document.activeElement;
      this.restoreFocus = true;
      this.previousScroll = { html: document.documentElement.style.overflow, body: document.body.style.overflow };
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      this.dialog.showModal();
      this.querySelector('[data-payment24-close]')?.focus({ preventScroll: true });
    }
    closeDialog(restoreFocus = true) {
      if (!this.dialog) return;
      this.restoreFocus = restoreFocus;
      if (this.dialog.open) this.dialog.close();
      this.finishDialog();
    }
    finishDialog() {
      if (this.previousScroll) {
        document.documentElement.style.overflow = this.previousScroll.html;
        document.body.style.overflow = this.previousScroll.body;
        this.previousScroll = null;
      }
      this.checkoutGeneration = (this.checkoutGeneration || 0) + 1;
      this.busy = false;
      if (this.consent) this.consent.checked = false;
      if (this.error) this.error.textContent = '';
      if (this.restoreFocus && this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
      this.previousFocus = null;
      this.refresh(false);
    }
    money(cents) {
      return new Intl.NumberFormat(this.config.locale || 'it', { style: 'currency', currency: this.config.currency }).format(cents / 100);
    }
    valid(variant, quantity) {
      if (this.pendingSelection || this.selectionFailed) return false;
      const term = variant?.term || this.term || 24;
      if (!this.eligibleTerms(variant?.totalCents).includes(term)) return false;
      if (this.config.currency !== 'EUR' || this.config.requiresSellingPlan || !variant?.ready || variant.hasQuantityBreaks) return false;
      if (!variant.depositId || !variant.depositAvailable || !variant.depositHandle) return false;
      if (!variant.sourceAvailable) return false;
      if (!Number.isSafeInteger(quantity) || quantity < variant.quantityMin || quantity > 99) return false;
      if ((quantity - variant.quantityMin) % variant.quantityIncrement !== 0) return false;
      if (variant.quantityMax && quantity > variant.quantityMax) return false;
      if (variant.depositTracked && quantity > variant.depositStock) return false;
      const total = variant.totalCents * quantity;
      const first = variant.depositCents * quantity;
      return Number.isSafeInteger(total) && Number.isSafeInteger(first) && first > 0 && total - first * (term - 1) > 0 && variant.depositCents === Math.round(variant.totalCents / term);
    }
    refresh(reset = true) {
      let { variant, quantity } = this.selected();
      const base = variant && this.config.variants[String(variant.id)];
      const allowedTerms = this.eligibleTerms(base?.totalCents);
      const mappedTerms = allowedTerms.filter(term => this.planVariant(base, term)?.ready);
      if (!mappedTerms.includes(this.term)) {
        const nextTerm = mappedTerms.includes(24) ? 24 : mappedTerms[0] || allowedTerms[0] || 12;
        if (this.term !== nextTerm) {
          this.term = nextTerm;
          this.checkoutGeneration = (this.checkoutGeneration || 0) + 1;
          this.busy = false;
          this.consent.checked = false;
          this.error.textContent = '';
          variant = this.planVariant(base, this.term);
        }
      }
      const key = this.key(variant, quantity);
      if (reset && key !== this.selectionKey) {
        this.checkoutGeneration = (this.checkoutGeneration || 0) + 1;
        this.busy = false;
        this.consent.checked = false;
        this.error.textContent = '';
      }
      this.selectionKey = key;
      const eligible = this.valid(variant, quantity);
      const belowMinimum = Number.isSafeInteger(base?.totalCents) && base.totalCents < 24000;
      this.hidden = !base || this.config.currency !== 'EUR' || (!belowMinimum && !mappedTerms.length);
      if (this.minimum) this.minimum.hidden = !belowMinimum;
      if (this.opener) {
        this.opener.hidden = belowMinimum;
        this.opener.disabled = !mappedTerms.some(term => this.valid(this.planVariant(base, term), quantity));
        this.opener.setAttribute('aria-disabled', this.opener.disabled ? 'true' : 'false');
      }
      for (const input of this.termInputs) {
        const term = Number(input.value);
        input.closest('.mtpay-option').hidden = !mappedTerms.includes(term);
        input.disabled = !this.valid(this.planVariant(base, term), quantity);
        input.checked = term === this.term;
      }
      const planLabel = this.scope.querySelector('[data-mt-installment-label]');
      if (planLabel) planLabel.textContent = mappedTerms.length ? this.label('availablePlans', { terms: mappedTerms.join(' / ') }) : this.label('minimumShort');
      this.button.disabled = !eligible || !this.consent.checked || this.busy;
      if (this.opener) this.button.textContent = this.busy ? this.label('verifying') : this.label('checkout', { term: this.term });
      if (!variant?.ready) return;
      const total = variant.totalCents * quantity;
      const first = variant.depositCents * quantity;
      const last = total - first * (this.term - 1);
      this.querySelector('[data-payment24-total]').textContent = this.money(total);
      this.querySelector('[data-payment24-today]').textContent = this.money(first);
      this.querySelector('[data-payment24-remaining]').textContent = this.money(total - first);
      const schedule = last === first ? this.config.labels.equalSchedule : this.config.labels.schedule;
      this.querySelector('[data-payment24-schedule]').textContent = this.textForTerm(schedule).replace('[regular]', this.money(first)).replace('[last]', this.money(last));
      const remainingLabel = this.querySelector('[data-payment24-remaining-label]');
      if (remainingLabel) remainingLabel.textContent = this.label('remainingLabel', { remaining: this.term - 1 });
      const notice = this.querySelector('[data-payment24-notice]');
      if (notice) notice.textContent = this.label('noticeText', { remaining: this.term - 1 });
      const consentLabel = this.querySelector('[data-payment24-consent-label]');
      if (consentLabel) consentLabel.textContent = this.label('consentText', { remaining: this.term - 1 });
      if (!eligible && !this.busy) this.error.textContent = this.config.labels.unavailable;
      else if (!this.busy && this.error.textContent === this.config.labels.unavailable) this.error.textContent = '';
    }
    encodeProperties(properties) {
      const bytes = new TextEncoder().encode(JSON.stringify(properties));
      let binary = '';
      for (const byte of bytes) binary += String.fromCharCode(byte);
      return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    }
    async verifyVariant(handle, id) {
      const rootPath = this.dataset.cartPath.replace(/cart\/?$/, '');
      const url = new URL(`${rootPath}products/${encodeURIComponent(handle)}`, location.origin);
      url.searchParams.set('variant', String(id));
      url.searchParams.set('section_id', 'mediatech-payment24-verify');
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const response = await fetch(url.href, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const variant = html.querySelector('[data-payment24-verification]');
      if (!variant || variant.dataset.id !== String(id) || variant.dataset.currency !== this.config.currency) throw new Error('unavailable');
      return { id: variant.dataset.id, price: Number(variant.dataset.price), available: variant.dataset.available === 'true', tracked: variant.dataset.tracked === 'true', stock: Number(variant.dataset.stock), sku: variant.dataset.sku };
    }
    async checkout() {
      const { variant, quantity } = this.selected();
      if (variant && this.key(variant, quantity) !== this.selectionKey) {
        this.refresh();
        return;
      }
      if (!this.valid(variant, quantity) || !this.consent.checked || this.busy) return;
      const generation = this.checkoutGeneration = (this.checkoutGeneration || 0) + 1;
      this.busy = true;
      this.refresh(false);
      this.error.textContent = '';
      try {
        const [liveSource, liveDeposit] = await Promise.all([
          this.verifyVariant(this.config.productHandle, variant.id),
          this.verifyVariant(variant.depositHandle, variant.depositId)
        ]);
        if (!this.isConnected || generation !== this.checkoutGeneration) return;
        if (!this.consent.checked) {
          this.busy = false;
          this.refresh(false);
          return;
        }
        if (!liveSource.available || liveSource.price !== variant.totalCents || !liveDeposit.available || liveDeposit.price !== variant.depositCents) throw new Error('changed');
        if (liveDeposit.sku !== `M${variant.term}-${variant.id}-${variant.totalCents}`) throw new Error('changed');
        if (liveDeposit.tracked && liveDeposit.stock < quantity) throw new Error('unavailable');
        const current = this.selected();
        if (current.variant?.id !== variant.id || current.quantity !== quantity || current.variant?.term !== variant.term) throw new Error('changed');
        const labels = this.config.labels;
        const properties = {
          [labels.orderProduct]: this.config.productTitle,
          [labels.orderTotal]: `${this.money(variant.totalCents)} ${labels.unit}`,
          [labels.orderFirst]: `${this.money(variant.depositCents)} ${labels.unit}`,
          [labels.orderRemaining]: `${this.money(variant.totalCents - variant.depositCents)} ${labels.unit}`,
          [labels.orderPlan]: this.textForTerm(labels.manual, variant.term),
          [labels.orderConsent]: this.textForTerm(labels.agreed, variant.term),
          _mediatech_source_variant: String(variant.id),
          _mediatech_total_cents_unit: String(variant.totalCents),
          _mediatech_first_cents_unit: String(variant.depositCents),
          _mediatech_last_cents_unit: String(variant.totalCents - variant.depositCents * (variant.term - 1)),
          _mediatech_term: String(variant.term),
          _mediatech_quantity: String(quantity),
          _mediatech_payment24: 'v2',
          _mediatech_source_url: this.config.productUrl
        };
        if (variant.title !== 'Default Title') properties[labels.orderOption] = variant.title;
        const checkoutUrl = new URL(`${this.dataset.cartPath}/${variant.depositId}:${quantity}`, location.origin);
        checkoutUrl.searchParams.set('properties', this.encodeProperties(properties));
        checkoutUrl.searchParams.set('attributes[Mediatech pagamento]', this.textForTerm(labels.cartAttribute, variant.term));
        location.assign(checkoutUrl.href);
      } catch (error) {
        if (!this.isConnected || generation !== this.checkoutGeneration) return;
        this.error.textContent = error.message === 'changed' ? this.config.labels.changed : this.config.labels.unavailable;
        this.busy = false;
        this.refresh(false);
      }
    }
  }
  customElements.define('mediatech-payment24', MediatechPayment24);
})();
